import { useLoginInfo } from 'hooks/useLoginInfo';
import { theader } from 'language/i18nkey';
import { LANG_EN, LANG_EN_US, LANG_KO_KR } from 'language/langutil';
import { useMemo, useState } from 'react';
import svcs from 'services';
import { useSvrInfo } from 'store/reducers/reducerMain';
import { countrylang, getLsPartnerLink } from 'util/langutil';
import { loginutil } from 'util/loginutil';
import { roleutil } from 'util/roleutil';
import { useZstdHeader } from '../useZstdHeader';
import { MenuItem } from './MenuItem';

export type MenuUrlId =
  | 'ai-chatbot' // ai chatbot
  | 'dlcenter' // download center
  | 'knowledge' // knowledge
  | 'qna' // qna
  | 'sample' // 샘플 라이브러리.
  | 'classroom' //
  | 'asset-insight' // asset insight
  | 'energy-simul' // 에너지 절감 시뮬레이터
  | 'remote' // 원격지원
  | 'drive' // 저압드라이브 제품선정가이드
  | 'hmi' // hmi
  | 'drive-panel-selector' // 드라이브 판넬(gear-box)
  | 'prod-selector' // 제품선정가이드
  | 'reducer' // 감속기 제품선정가이드.
  | 'linear-motor' // 리니어 모터
  | 'news-event' // 뉴스/이벤트
  | 'tech-square' // tech square
  | 'ls-electric'
  | 'ls-electric-global'
  | 'ls-electric-japan'
  | 'ls-electric-china'
  | 'ls-electric-korea'
  | 'ls-electric-america'
  | 'rnd'
  | 'partner'
  | 'test-report'
  | 'bookmark'
  | 'compliance-sustainability'
  | 'ppwr'
  | 'cra';
type MenuUrlInfo = {
  menuId?: MenuUrlId;
  to?: string; // 상대패스.
  href?: string; // 외부 링크.
  target?: string; // 외부 링크 타겟.
  label: string;
};

type MenuUrlType = 'menu' | 'quicklink' | 'footer';

type MenuAuth = 'all' | 'employ' | 'ptnr';

const menuEnabledKr: Map<MenuUrlId, MenuAuth[]> = new Map<MenuUrlId, MenuAuth[]>([
  ['asset-insight', ['all']],
  ['energy-simul', ['all']],
  ['qna', ['all']],
  ['drive-panel-selector', ['all']],
  ['prod-selector', ['all']],
  ['test-report', undefined],
  ['linear-motor', ['all']],
  ['remote', ['all']],
]);

const menuEnabledUs: Map<MenuUrlId, MenuAuth[]> = new Map<MenuUrlId, MenuAuth[]>([
  ['asset-insight', ['employ']],
  ['energy-simul', ['employ']],
  ['qna', undefined], // qna 없음.
  ['drive-panel-selector', undefined], // drive-panel-selector 없음.
  ['prod-selector', undefined], // prod-selector 없음.
  ['test-report', ['all']],
  ['linear-motor', undefined],
  ['remote', undefined],
]);

const menuEnabledEn: Map<MenuUrlId, MenuAuth[]> = new Map<MenuUrlId, MenuAuth[]>([
  ['asset-insight', ['employ']],
  ['energy-simul', ['employ']],
  ['qna', ['all']],
  ['drive-panel-selector', undefined], // drive-panel-selector 없음.
  ['prod-selector', undefined], // prod-selector 없음.
  ['test-report', ['all']], // test-report 없음.
  ['linear-motor', ['all']],
  ['remote', ['all']],
  ['partner', ['ptnr', 'employ']],
]);
const getMenuSet = (lang: string): Map<MenuUrlId, MenuAuth[]> => {
  if (lang === LANG_KO_KR) {
    return menuEnabledKr;
  }
  if (lang === LANG_EN_US) {
    return menuEnabledUs;
  }
  return menuEnabledEn;
};

const useMenuUrl = ({
  type,
}: {
  type: MenuUrlType;
}): { getMenuUrl: (menu: MenuUrlId) => MenuUrlInfo; menuEnabled: (menu: MenuUrlId) => boolean } => {
  const { svrConf } = useSvrInfo();
  const loginInfo = useLoginInfo();
  const { isPtnrUser } = useZstdHeader();
  const menuEnabled = (menu: MenuUrlId): boolean => {
    const lang = svcs.svcLang.lang();
    if (menu === 'rnd') {
      // 알록달록연구소(Colorful LS)는 한국어 + RND 역할 보유자에게만 노출.
      return lang === LANG_KO_KR && roleutil.hasRole('RND', loginInfo?.userRoles);
    }
    const set = getMenuSet(lang);
    if (!set.has(menu)) return true; // 관리하지 않음.

    const auth = set.get(menu);
    if (!auth) return false; // 권한없음 : 모두 접근 불가.

    if (auth.includes('all')) return true; // all : 모두 접근 가능.
    if (auth.includes('ptnr')) {
      // 파트너 접근가능.
      if (isPtnrUser()) return true;
    }
    if (auth.includes('employ')) {
      // employ : 임직원 접근 가능.
      if (loginutil.islse(loginInfo)) return true;
    }

    return false;
  };
  const menuLabels: Map<MenuUrlId, string[]> = useMemo(() => {
    const labels = new Map<MenuUrlId, string[]>([
      ['ai-chatbot', ['AI 챗봇', 'AI ChatBot']],
      ['dlcenter', ['다운로드 센터', 'Download Center']],
      ['classroom', ['실시간 강의실', 'Online Training']],
      ['knowledge', ['Knowledge Base', 'Knowledge Base']],
      ['rnd', ['Colorful LS', 'Colorful LS']],
      ['partner', ['Service Portal', 'Service Portal']],
      ['sample', ['샘플 라이브러리', 'Sample Library']],
      ['qna', ['Q&A', 'Q&A']],
      ['asset-insight', ['Asset Insight (Beta)', 'Asset Insight (Beta)']],
      ['energy-simul', ['에너지 절감 시뮬레이터', 'Energy Saving Simulator']],
      ['remote', ['원격지원', 'Remote Technical Support']],
      ['bookmark', ['북마크', 'Bookmark']],
      ['hmi', type === 'menu' ? ['HMI', 'HMI'] : ['HMI Selector', 'HMI Selector']],
      ['drive', type === 'menu' ? ['저압 드라이브', 'Drive'] : ['저압 드라이브 Selector', 'Drive Selector']],
      ['test-report', [theader('I18N_TITLE_SERVICE_REQUEST_TEST_REPORT')].reduce((acc, cur) => [cur, cur], [])],
      ['reducer', type === 'menu' ? ['감속기', 'Gear Box'] : ['감속기 Selector', 'Gear Box Selector']],
      [
        'linear-motor',
        type === 'menu' ? ['리니어 모터', 'Linear Motor'] : ['리니어 모터 Selector', 'Linear Motor Selector'],
      ],
      [
        'drive-panel-selector',
        type === 'menu'
          ? ['표준 패널 드라이브', 'Drive Panel']
          : ['표준 패널 드라이브 Selector', 'Drive Panel Selector'],
      ],
      ['news-event', ['뉴스/이벤트', 'News/Events']],
      ['tech-square', ['Tech Square', 'Tech Square']],
      ['ls-electric', ['LS ELECTRIC', 'LS ELECTRIC']],
      ['prod-selector', ['Product Selector', 'Product Selector']],
      ['ls-electric-global', ['LS ELECTRIC Global', 'LS ELECTRIC Global']],
      ['ls-electric-japan', ['LS ELECTRIC Japan', 'LS ELECTRIC Japan']],
      ['ls-electric-china', ['LS ELECTRIC China', 'LS ELECTRIC China']],
      ['ls-electric-korea', ['LS ELECTRIC Korea', 'LS ELECTRIC Korea']],
      ['ls-electric-america', ['LS ELECTRIC America', 'LS ELECTRIC America']],
      ['compliance-sustainability', ['Compliance & Sustainability', 'Compliance & Sustainability']],
      ['ppwr', ['Packaging Regulation (PPWR)', 'Packaging Regulation (PPWR)']],
      ['cra', ['Cyber Resilience Act (CRA)', 'Cyber Resilience Act (CRA)']]
    ]);

    return labels;
  }, [type]);
  const fnMenuMap: Map<MenuUrlId, (menuId: MenuUrlId) => MenuUrlInfo> = useMemo(() => {
    const lang = svcs.svcLang.lang();
    const getLabel = (label: string[]) => {
      // [ww, en, us]
      if (lang === LANG_KO_KR) {
        return label[0];
      }
      if (lang === LANG_EN_US) {
        if (label[2]) return label[2];
      }
      if (label[1]) return label[1];
      return label[0];
    };
    const map = new Map<MenuUrlId, (menuId: MenuUrlId) => MenuUrlInfo>([
      [
        'ai-chatbot',
        (menuId: MenuUrlId) => {
          const href = svrConf.chatbotUrl;
          const label = getLabel(menuLabels.get(menuId));
          return { menuId, href, label, target: '_blank' };
        },
      ],
      [
        'dlcenter',
        (menuId: MenuUrlId) => {
          return { menuId, to: countrylang('/dlcenter'), label: getLabel(menuLabels.get(menuId)) };
        },
      ],
    ]);
    return map;
  }, [menuLabels]);
  const getMenuUrl = (menu: MenuUrlId): MenuUrlInfo => {
    const fn = fnMenuMap.get(menu);
    if (fn) return fn(menu);

    const lang = svcs.svcLang.lang();
    const getLabel = (label: string[]) => {
      // [ww, en, us]
      if (lang === LANG_KO_KR) {
        return label[0];
      }
      if (lang === LANG_EN_US) {
        if (label[2]) return label[2];
      }
      if (label[1]) return label[1];
      return label[0];
    };
    if (menu === 'classroom') {
      const label = menuLabels.get(menu);
      return { menuId: menu, to: countrylang('/service/classroom'), label: getLabel(label) };
    } else if (menu === 'knowledge') {
      const label = menuLabels.get(menu);
      return { menuId: menu, to: countrylang('/community/blog'), label: getLabel(label) };
    } else if (menu === 'rnd') {
      const label = menuLabels.get(menu);
      return { menuId: menu, to: countrylang('/community/rnd'), label: getLabel(label) };
    } else if (menu === 'partner') {
      const label = menuLabels.get(menu);
      const url = getLsPartnerLink();
      return { menuId: menu, to: url, label: getLabel(label) };
    } else if (menu === 'sample') {
      const label = menuLabels.get(menu);
      return { menuId: menu, to: countrylang('/service/sample'), label: getLabel(label) };
    } else if (menu === 'qna') {
      const label = menuLabels.get(menu);
      return { menuId: menu, to: countrylang('/community/qna'), label: getLabel(label) };
    } else if (menu === 'asset-insight') {
      const label = menuLabels.get(menu);
      return { menuId: menu, to: countrylang('/service/assetinsight'), label: getLabel(label) };
    } else if (menu === 'energy-simul') {
      const label = menuLabels.get(menu);
      return { menuId: menu, to: countrylang('/service/energysave'), label: getLabel(label) };
    } else if (menu === 'remote') {
      const label = menuLabels.get(menu);
      return { menuId: menu, to: countrylang('/service/support'), label: getLabel(label) };
    } else if (menu === 'bookmark') {
      const label = menuLabels.get(menu);
      return { menuId: menu, to: countrylang('/myPage?menu=bookmark'), label: getLabel(label) };
    } else if (menu === 'hmi') {
      const label = menuLabels.get(menu);
      return { menuId: menu, href: countrylang('/productselector?view=hmi'), label: getLabel(label) };
    } else if (menu === 'drive') {
      const label = menuLabels.get(menu);
      return { menuId: menu, href: countrylang('/productselector?view=drive'), label: getLabel(label) };
    } else if (menu === 'test-report') {
      return {
        menuId: menu,
        href: 'https://pqms.ls-electric.com/trmLogin.lse',
        label: getLabel(menuLabels.get(menu)),
        target: '_blank',
      };
    } else if (menu === 'reducer') {
      let href = 'https://www.ls-electric.com/gearbox/ko/motor';
      if (lang === LANG_EN_US) {
        href = 'https://www.ls-electric.com/gearbox/en/motor';
      } else if (lang === LANG_EN) {
        href = 'https://www.ls-electric.com/gearbox/en/motor';
      }
      const label = menuLabels.get(menu);
      return { menuId: menu, href, label: getLabel(label), target: '_blank' };
    } else if (menu === 'linear-motor') {
      let href = 'https://www.ls-electric.com/linear_motor/ko/product-search/step1';
      if (lang === LANG_EN_US) {
        href = 'https://www.ls-electric.com/linear_motor/en/product-search/step1';
      } else if (lang === LANG_EN) {
        //href = '';
        href = 'https://www.ls-electric.com/linear_motor/en/product-search/step1';
      }
      const label = menuLabels.get(menu);
      return { menuId: menu, href, label: getLabel(label), target: '_blank' };
    } else if (menu === 'drive-panel-selector') {
      // if ([LANG_EN_US, LANG_EN].includes(lang)) return { label: null };
      // const label = ['드라이브 판넬', 'Drive Panel'];
      const label = menuLabels.get(menu);
      return { to: countrylang('/drive-panel-selector'), label: getLabel(label) };
    } else if (menu === 'ppwr') {
      const label = menuLabels.get(menu);
      return { menuId: menu, to: countrylang('/compliance/ppwr'), label: getLabel(label) };
    } else if (menu === 'cra') {
      const label = menuLabels.get(menu);
      return { menuId: menu, to: countrylang('/compliance/cra'), label: getLabel(label) };
    } else if (menu === 'compliance-sustainability') {
      const label = menuLabels.get(menu);
      return { menuId: menu, label: getLabel(label) };
    } else if (menu === 'news-event') {
      const label = menuLabels.get(menu);
      return { menuId: menu, to: countrylang('/community/board'), label: getLabel(label) };
    } else if (menu === 'tech-square') {
      let href = 'https://tech-square.co.kr/';
      if (lang === LANG_EN_US) {
        href = '';
      } else if (lang === LANG_EN) {
        href = '';
      }
      const label = menuLabels.get(menu);
      return { menuId: menu, href, label: getLabel(label), target: '_blank' };
    } else if (menu === 'ls-electric') {
      let href = 'https://www.ls-electric.com/ko';
      if (lang === LANG_EN_US) {
        href = 'https://www.lselectricamerica.com/';
      } else if (lang === LANG_EN) {
        href = 'https://www.ls-electric.com/';
      }
      const label = menuLabels.get(menu);
      return { menuId: menu, href, label: getLabel(label), target: '_blank' };
    } else if (menu === 'prod-selector') {
      const label = menuLabels.get(menu);

      return { menuId: menu, label: getLabel(label), to: countrylang('/productselector') };
    } else if (menu === 'ls-electric-global') {
      const label = menuLabels.get(menu);
      return { menuId: menu, href: 'https://www.ls-electric.com/', label: getLabel(label), target: '_blank' };
    } else if (menu === 'ls-electric-japan') {
      const label = menuLabels.get(menu);
      return { menuId: menu, href: 'https://www.ls-electric.com/', label: getLabel(label), target: '_blank' };
    } else if (menu === 'ls-electric-china') {
      const label = menuLabels.get(menu);
      return { menuId: menu, href: 'https://www.ls-electric.com/', label: getLabel(label), target: '_blank' };
    } else if (menu === 'ls-electric-korea') {
      const label = menuLabels.get(menu);
      return { menuId: menu, href: 'https://www.ls-electric.com/ko', label: getLabel(label), target: '_blank' };
    } else if (menu === 'ls-electric-america') {
      const label = menuLabels.get(menu);
      return { menuId: menu, href: 'https://www.lselectricamerica.com/', label: getLabel(label), target: '_blank' };
    }

    throw new Error('Not implemented : ' + menu);
  };

  return { getMenuUrl, menuEnabled };
};

export default useMenuUrl;

export const getToOrHref = (menu: MenuUrlInfo): string => {
  if (menu.to) return menu.to;
  if (menu.href) return menu.href;
  return '';
};

// 다른 origin을 가리키면 외부 링크. 상대경로(hmi/drive의 /productselector 등)는 내부로 처리된다.
export const isExternalUrl = (url?: string): boolean => {
  if (!url) return false;
  try {
    return new URL(url, window.location.origin).origin !== window.location.origin;
  } catch {
    return false;
  }
};

export const MenuItemWrap = ({
  menuId,
  onClick,
  type,
}: {
  menuId: MenuUrlId;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  type: MenuUrlType;
}) => {
  const { getMenuUrl } = useMenuUrl({ type });
  const [menu] = useState<MenuUrlInfo>(getMenuUrl(menuId));
  if (!menu.to && !menu.href) return null;
  if (menu.to) return <MenuItem label={menu.label} to={menu.to} onClick={onClick} />;
  return <MenuItem label={menu.label} href={menu.href} target={menu.target} onClick={onClick} isExternal={isExternalUrl(menu.href)} />;
};
